import { ManageSearchOutlined } from "@mui/icons-material";
import { Divider, IconButton, Tooltip } from "@mui/material";
import {
  FC,
  MouseEventHandler,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { AccessControl } from "@ryde/controls";
import {
  useDropdownSourceContext,
  useRidesRecalculatePrice,
  useRtl,
} from "@ryde/redux";
import {
  ActionsEnum,
  currencySignSelector,
  DrawerType,
  formatPrice,
  IPriceDetailLine,
  IRideDto,
  NO_VALUE_DISPLAY,
  onActivateKeyDown,
  SINGLE_KEY,
  useDrawers,
} from "@ryde/shared";

import {
  buildDisplayedPriceDetails,
  shouldDisplayClientRidePriceBreakdownLines,
} from "./ride-header-price-dropdown.utils";
import { RideHeaderPriceDropdownStyled } from "./ride-header.styled";

interface IRideHeaderPriceDropdown {
  rideDto?: Partial<IRideDto>;
  handlePriceRateDrawerOpen: (id: number) => Promise<void>;
  onClosePopover?: () => void;
}

export const RideHeaderPriceDropdown: FC<IRideHeaderPriceDropdown> = ({
  rideDto,
  handlePriceRateDrawerOpen,
  onClosePopover,
}) => {
  const { t, i18n } = useTranslation();
  const { getDropdownLabel } = useDropdownSourceContext();
  const { handleRecalculatePriceClick } = useRidesRecalculatePrice();
  const { openDrawer } = useDrawers();
  const [priceRateName, setPriceRateName] = useState(NO_VALUE_DISPLAY);
  const currencySign = useRecoilValue(currencySignSelector);
  const { isRtl } = useRtl();
  const [, startTransition] = useTransition();
  const priceDir = isRtl ? "ltr" : undefined;

  const onDropdownOpenClick: MouseEventHandler<SVGSVGElement> = e => {
    e.preventDefault();

    if (!rideDto?.priceRateId) {
      return;
    }

    handlePriceRateDrawerOpen(rideDto.priceRateId || 0);
  };

  const handleOpenPriceSearchLogs = useEvent((): void => {
    if (!rideDto?.id) {
      return;
    }
    onClosePopover?.();
    openDrawer({
      drawerProps: {
        type: DrawerType.PriceRateSearchLogs,
        rideId: rideDto.id,
        supplierId: rideDto.supplierId ?? null,
        vehicleTypeId: rideDto.vehicleTypeId ?? null,
        priceRateId: rideDto.priceRateId ?? null,
      },
    });
  });

  useEffect(() => {
    const loadSupplierName = async (): Promise<void> => {
      if (!rideDto?.priceRateId) {
        setPriceRateName(NO_VALUE_DISPLAY);
      } else {
        const priceRateLabel = await getDropdownLabel(
          "priceRate",
          rideDto?.priceRateId,
        );

        setPriceRateName(priceRateLabel);
      }
    };

    startTransition(() => {
      loadSupplierName();
    });
  }, [getDropdownLabel, rideDto?.priceRateId]);

  const isRecalculatePriceButtonDisplayed = Boolean(
    rideDto?.metadata?.stationIds?.length,
  );

  const displayedPriceDetails: IPriceDetailLine[] = useMemo(() => {
    if (!shouldDisplayClientRidePriceBreakdownLines(rideDto)) {
      return [];
    }
    return buildDisplayedPriceDetails(rideDto?.metadata?.priceDetails);
  }, [rideDto]);

  const priceSearchLogsTooltip = t("ride.priceRateSearchLogsTitle");

  return (
    rideDto && (
      <RideHeaderPriceDropdownStyled>
        <div className="headline">
          <span className="priceRateText">
            {t(`priceRate.${SINGLE_KEY}`)}{" "}
            {priceRateName || rideDto.priceRateId || NO_VALUE_DISPLAY}
          </span>
          {!!rideDto?.priceRateId && (
            <AccessControl entity="priceRate" action={ActionsEnum.Read}>
              <SVG_MUI.SquareIconUpRight4SVG
                className="linkOut"
                onClick={onDropdownOpenClick}
              />
            </AccessControl>
          )}
        </div>

        {(typeof rideDto?.metadata?.manualPrice === "number" ||
          !!rideDto?.metadata?.manualPrice) && (
          <div className="price_row">
            <span>{t("ride.formLabel.manualPrice")}</span>
            <span className="price" dir={priceDir}>
              {`${currencySign}${formatPrice({ value: rideDto?.metadata.manualPrice ?? 0 })}`}
            </span>
          </div>
        )}

        {displayedPriceDetails?.map((row, idx) => {
          const labelKey = `ride.formLabel.${row.label}`;
          const labelText = i18n.exists(labelKey) ? t(labelKey) : row.label;
          return (
            <div className="price_row" key={`${row.label}-${idx}`}>
              <span>{labelText}</span>
              {!!row.calcDetails && ` (${row.calcDetails})`}
              <span className="price" dir={priceDir}>
                {`${currencySign}${formatPrice({ value: row.value })}`}
              </span>
            </div>
          );
        })}

        <div className="total_row">
          <span>{t("ride.formLabel.price")}</span>
          <span className="total" dir={priceDir}>
            {`${currencySign}${formatPrice({ value: rideDto?.price ?? 0 })}`}
          </span>
        </div>

        <AccessControl entity="system" action="update_prices">
          {Boolean(rideDto?.id) && (
            <>
              <Divider className="divider" orientation="horizontal" />
              <div className="recalculate_price_row">
                <Tooltip title={priceSearchLogsTooltip}>
                  <IconButton
                    type="button"
                    size="small"
                    className="price_rate_search_logs_btn"
                    onClick={handleOpenPriceSearchLogs}
                    aria-label={priceSearchLogsTooltip}
                  >
                    <ManageSearchOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
                {isRecalculatePriceButtonDisplayed && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={handleRecalculatePriceClick}
                    onKeyDown={onActivateKeyDown(handleRecalculatePriceClick)}
                    className="recalculate_price_text"
                  >
                    {t(`general.RECALCULATE_PRICE`)}{" "}
                  </span>
                )}
              </div>
            </>
          )}
        </AccessControl>
      </RideHeaderPriceDropdownStyled>
    )
  );
};
